/**
 * ProductsTable Component - Story 6.2: Create My Products Page
 * Data table component with shadcn/ui, filters, search, and actions
 */

import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Edit,
  Eye,
  MessageCircle,
  Trash2,
  AlertTriangle,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProductStatusBadgeCompact } from './ProductStatusBadge';
import { ProductStatusToggle } from './ProductStatusToggle';
import { QuantityBadge } from './QuantityBadge';
import type { ProductsTableProps, ProductRow } from '../types/products.types';

/**
 * Table skeleton loader
 */
const ProductsTableSkeleton: React.FC = () => (
  <div className="space-y-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-12 w-full" />
    ))}
  </div>
);

/**
 * Table error state
 */
const ProductsTableError: React.FC<{
  error?: { message: string };
  onRetry?: () => void;
}> = ({ error, onRetry }) => (
  <Alert variant="destructive" className="mt-4">
    <AlertTriangle className="h-4 w-4" />
    <AlertDescription className="ml-2 flex items-center justify-between">
      <span>{error?.message || 'Failed to load products'}</span>
      {onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry} className="ml-4">
          Retry
        </Button>
      )}
    </AlertDescription>
  </Alert>
);

/**
 * Empty state
 */
const ProductsTableEmpty: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-12">
    <MessageCircle className="h-12 w-12 text-gray-300 mb-4" />
    <h3 className="text-lg font-medium text-gray-700">No products found</h3>
    <p className="text-gray-500 text-sm mt-2">Try adjusting your filters or search criteria</p>
  </div>
);

/**
 * Responsive product card for mobile
 */
const ProductCard: React.FC<{
  product: ProductRow;
  onView?: () => void;
  onEdit?: () => void;
  onViewInquiries?: () => void;
  onDelete?: () => void;
}> = ({ product, onView, onEdit, onViewInquiries, onDelete }) => (
  <Card className="p-4 mb-4">
    <div className="space-y-3">
      {/* Product image and name */}
      <div className="flex gap-3">
        {product.image ? (
          <div className="relative">
            <img
              src={product.image}
              alt={product.name}
              className="w-16 h-16 object-cover rounded border border-gray-200"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.classList.remove('hidden');
              }}
            />
            <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center hidden">
              <span className="text-xs text-gray-500">No img</span>
            </div>
          </div>
        ) : (
          <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-xs text-gray-500">No img</span>
          </div>
        )}
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 line-clamp-2">{product.name}</h4>
          <p className="text-sm text-gray-500 mt-1">{product.category}</p>
        </div>
      </div>

      {/* Status and stats */}
      <div className="flex items-center justify-between">
        <ProductStatusBadgeCompact status={product.status} />
        <span className="text-sm text-gray-600">{product.inquiries} inquiries</span>
      </div>

      {/* Quantity information */}
      <QuantityBadge
        totalQuantity={product.totalQuantity}
        availableQuantity={product.availableQuantity}
        unit={product.unit}
        compact
        showAlert
      />

      {/* Created date */}
      <p className="text-xs text-gray-500">Created: {product.createdDate}</p>

      {/* Action buttons */}
      <div className="flex gap-2 pt-2 border-t">
        {onView && (
          <Button size="sm" variant="outline" onClick={onView} className="flex-1">
            <Eye className="w-4 h-4 mr-2" />
            View
          </Button>
        )}
        {onEdit && (
          <Button size="sm" variant="outline" onClick={onEdit} className="flex-1">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        )}
        {onViewInquiries && (
          <Button size="sm" variant="outline" onClick={onViewInquiries} className="flex-1">
            <MessageCircle className="w-4 h-4 mr-2" />
            Inquiries
          </Button>
        )}
        {onDelete && (
          <Button
            size="sm"
            variant="destructive"
            onClick={onDelete}
            className="flex-1"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        )}
      </div>
    </div>
  </Card>
);

/**
 * ProductsTable Component
 * Data table with sorting, pagination, and actions
 *
 * @component
 * @example
 * <ProductsTable
 *   products={products}
 *   isLoading={isLoading}
 *   totalPages={totalPages}
 *   currentPage={currentPage}
 *   onEditClick={(action) => navigate(`/edit/${action.productId}`)}
 *   onPageChange={(page) => updateFilters({ page })}
 * />
 */
export const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  isLoading,
  isError,
  error,
  totalPages,
  currentPage,
  onViewClick,
  onEditClick,
  onViewInquiriesClick,
  onDeleteClick,
  onStatusChange,
  onStatusError,
  onPageChange,
  onRowClick,
  onRetry,
  sortBy,
  onSortChange,
}) => {
  // Mobile breakpoint - show cards instead of table
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle sort change
  const handleSort = (field: string) => {
    if (onSortChange) {
      // If clicking same field, toggle order; otherwise default to desc
      const newOrder = sortBy === field ? 'asc' : 'desc';
      onSortChange(field, newOrder);
    }
  };

  // Sort column header
  const SortableHeader: React.FC<{ field: string; label: string }> = ({ field, label }) => (
    <TableHead
      className="cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {label}
        {sortBy === field && <ArrowUpDown className="w-4 h-4" />}
      </div>
    </TableHead>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="rounded-lg border">
        <div className="p-6">
          <ProductsTableSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return <ProductsTableError error={error || undefined} onRetry={onRetry} />;
  }

  // Empty state
  if (!products || products.length === 0) {
    return (
      <div className="rounded-lg border">
        <div className="p-6">
          <ProductsTableEmpty />
        </div>
      </div>
    );
  }

  // Mobile view - cards
  if (isMobile) {
    return (
      <div className="space-y-4">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onView={
              onViewClick
                ? () =>
                    onViewClick({
                      productId: product.id,
                      action: 'view',
                      productName: product.name,
                    })
                : undefined
            }
            onEdit={
              onEditClick
                ? () =>
                    onEditClick({
                      productId: product.id,
                      action: 'edit',
                      productName: product.name,
                    })
                : undefined
            }
            onViewInquiries={
              onViewInquiriesClick
                ? () =>
                    onViewInquiriesClick({
                      productId: product.id,
                      productName: product.name,
                      inquiryCount: product.inquiries,
                    })
                : undefined
            }
            onDelete={
              onDeleteClick
                ? () =>
                    onDeleteClick({
                      productId: product.id,
                      productName: product.name,
                    })
                : undefined
            }
          />
        ))}

        {/* Mobile pagination */}
        <div className="flex items-center justify-between pt-4">
          <Button
            size="sm"
            variant="outline"
            disabled={currentPage === 1}
            onClick={() => onPageChange?.(currentPage - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange?.(currentPage + 1)}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Desktop view - table
  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50">
              <TableHead>Image</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Available Quantity</TableHead>
              <SortableHeader field="status" label="Status" />
              <SortableHeader field="inquiries" label="Inquiries" />
              <SortableHeader field="createdDate" label="Created Date" />
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow
                key={product.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onRowClick?.(product.id)}
              >
                {/* Image */}
                <TableCell className="w-16">
                  {product.image ? (
                    <div className="relative">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded border border-gray-200"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.classList.remove('hidden');
                        }}
                      />
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center hidden">
                        <span className="text-xs text-gray-500">No img</span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      <span className="text-xs text-gray-500">No img</span>
                    </div>
                  )}
                </TableCell>

                {/* Product Name */}
                <TableCell className="font-medium max-w-xs">
                  <span className="line-clamp-2">{product.name}</span>
                </TableCell>

                {/* Category */}
                <TableCell className="text-gray-600 text-sm">{product.category}</TableCell>

                {/* Available Quantity */}
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <QuantityBadge
                    totalQuantity={product.totalQuantity}
                    availableQuantity={product.availableQuantity}
                    unit={product.unit}
                    compact
                    showAlert
                  />
                </TableCell>

                {/* Status - Story 6.6: Toggle Switch with Low Stock Automation */}
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <ProductStatusToggle
                    productId={product.id}
                    productName={product.name}
                    currentStatus={product.status}
                    availableQuantity={product.availableQuantity}
                    onStatusChange={onStatusChange}
                    onError={onStatusError}
                  />
                </TableCell>

                {/* Inquiries */}
                <TableCell className="text-center text-gray-600">
                  {product.inquiries}
                </TableCell>

                {/* Created Date */}
                <TableCell className="text-gray-600 text-sm">{product.createdDate}</TableCell>

                {/* Actions */}
                <TableCell
                  className="space-x-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {onViewClick && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        onViewClick({
                          productId: product.id,
                          action: 'view',
                          productName: product.name,
                        })
                      }
                      title="View product"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  {onEditClick && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        onEditClick({
                          productId: product.id,
                          action: 'edit',
                          productName: product.name,
                        })
                      }
                      title="Edit product"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  {onViewInquiriesClick && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        onViewInquiriesClick({
                          productId: product.id,
                          productName: product.name,
                          inquiryCount: product.inquiries,
                        })
                      }
                      title="View inquiries"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  )}
                  {onDeleteClick && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() =>
                        onDeleteClick({
                          productId: product.id,
                          productName: product.name,
                        })
                      }
                      title="Delete product"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-4 border-t bg-gray-50">
        <div className="text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={currentPage === 1}
            onClick={() => onPageChange?.(currentPage - 1)}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange?.(currentPage + 1)}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export type { ProductsTableProps };
